import React from 'react';
import clsx from 'clsx';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import Title, {type TitleProps} from '@netx/core-components/Title';
import {inputClasses} from '@mui/material/Input';
import {searchFilterClasses} from '@netx/core-components/SearchFilter';
import {searchFilterExpandableClasses} from '@netx/core-components/SearchFilterExpandable';
import {
	getSystemViewUtilityClass,
	SYSTEM_VIEW_CLASS_NAME,
	systemViewClasses,
	type SystemViewClasses,
} from './systemViewClasses';

type SystemViewOwnerState = WithClasses<SystemViewClasses>;

const useUtilityClasses = (ownerState: SystemViewOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		content: ['content'],
		controls: ['controls'],
		extras: ['extras'],
		filters: ['filters'],
		header: ['header'],
		title: ['title'],
	};
	return composeClasses(slots, getSystemViewUtilityClass, classes);
};

export const SystemViewRoot = styled('div', {
	name: SYSTEM_VIEW_CLASS_NAME,
	slot: 'Root',
	overridesResolver: (props, styles) => styles.root,
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	overflow: 'hidden',
	minHeight: 0,
}));

const SystemViewHeader = styled('div', {
	name: SYSTEM_VIEW_CLASS_NAME,
	slot: 'Header',
	overridesResolver: (props, styles) => styles.header,
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	padding: theme.spacing(2),
	minHeight: 72,
	gap: 16,
	alignItems: 'center',
	overflow: 'hidden',
	[theme.breakpoints.down('md')]: {
		flexDirection: 'column',
		alignItems: 'flex-start',
	},
}));

export const SystemViewHeaderTitle = styled(Title, {
	name: SYSTEM_VIEW_CLASS_NAME,
	slot: 'Title',
	overridesResolver: (props, styles) => styles.title,
})(({theme}) => ({
	flex: '1 1 auto',
}));

export const SystemViewRootHeaderExtras = styled('div', {
	name: SYSTEM_VIEW_CLASS_NAME,
	slot: 'Extras',
	overridesResolver: (props, styles) => styles.extras,
})(({theme}) => ({
	flex: '0 0 auto',
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	gap: 12,
	[theme.breakpoints.down('md')]: {
		flexDirection: 'column',
		width: '100%',
		'&:not(:empty)': {
			marginTop: theme.spacing(1),
		},
	},
}));

export const SystemViewRootHeaderControls = styled('div', {
	name: SYSTEM_VIEW_CLASS_NAME,
	slot: 'Controls',
	overridesResolver: (props, styles) => styles.controls,
})(({theme}) => ({
	flex: '0 0 auto',
	display: 'flex',
	justifyContent: 'flex-start',
	gap: 12,
	alignSelf: 'stretch',
	alignItems: 'stretch',
	'& .MuiIconButton-edgeEnd': {
		marginRight: -8,
	},
	[theme.breakpoints.down('md')]: {
		justifyContent: 'flex-end',
	},
}));

export const SystemViewRootHeaderFilters = styled('div', {
	name: SYSTEM_VIEW_CLASS_NAME,
	slot: 'Filters',
	overridesResolver: (props, styles) => styles.filters,
})(({theme}) => ({
	flex: '0 0 auto',
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	gap: 12,
	[theme.breakpoints.down('md')]: {
		flexDirection: 'column',
		width: '100%',
		paddingLeft: '0 !important',
		[`& .${searchFilterClasses.root}, & .${inputClasses.input}`]: {
			width: '100% !important',
		},
		[`& .${searchFilterExpandableClasses.root}`]: {
			transition: 'none !important',
			width: '100%',
		},
	},
}));

export const SystemViewContent = styled('div', {
	name: SYSTEM_VIEW_CLASS_NAME,
	slot: 'Content',
	overridesResolver: (props, styles) => styles.content,
})(({theme}) => ({
	display: 'flex',
	// flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
}));

export interface SystemViewProps extends WithClasses<SystemViewClasses> {
	children?: React.ReactNode;
	bulkActions?: React.ReactNode;
	title: string;
	controls?: React.ReactNode;
	TitleProps?: Partial<TitleProps>;
	filters?: React.ReactNode;
}

export const SystemViewPrimitive = (
	{
		classes: _classes,
		className,
		bulkActions,
		children,
		controls,
		filters,
		title,
		TitleProps,
		...props
	}: SystemViewProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const ownerState: SystemViewOwnerState = {classes: _classes, className};

	const classes = useUtilityClasses(ownerState);

	return (
		<SystemViewRoot
			className={clsx(classes.root, className)}
			ownerState={ownerState}
			ref={ref}
			{...props}
		>
			<SystemViewHeader className={classes.header}>
				<SystemViewHeaderTitle
					className={classes.title}
					primary={title}
					{...TitleProps}
				/>

				{bulkActions ? (
					<SystemViewRootHeaderControls
						className={classes.controls}
						style={{
							width: '100%',
							paddingLeft: 42,
						}}
					>
						{bulkActions}
					</SystemViewRootHeaderControls>
				) : (
					<SystemViewRootHeaderExtras className={classes.extras}>
						{filters && (
							<SystemViewRootHeaderFilters className={classes.filters}>
								{filters}
							</SystemViewRootHeaderFilters>
						)}
						{controls && (
							<SystemViewRootHeaderControls className={classes.controls}>
								{controls}
							</SystemViewRootHeaderControls>
						)}
					</SystemViewRootHeaderExtras>
				)}
			</SystemViewHeader>

			<SystemViewContent className={classes.content}>
				{children}
			</SystemViewContent>
		</SystemViewRoot>
	);
};

export const SystemView = React.forwardRef<HTMLDivElement, SystemViewProps>(
	SystemViewPrimitive,
);

export default SystemView;
